import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { portalHomeFor, type OnboardingStep, errorWords } from "@alm/domain";
import { BillingOnWeb } from "@/components/app/onboarding/billing-on-web";
import { CommunityStep } from "@/components/app/onboarding/community-step";
import { DoneStep } from "@/components/app/onboarding/done-step";
import { IntentStep } from "@/components/app/onboarding/intent-step";
import { OnboardingFrame } from "@/components/app/onboarding/onboarding-frame";
import { PaymentStep } from "@/components/app/onboarding/payment-step";
import { PasswordStep } from "@/components/app/onboarding/password-step";
import { PhoneStep } from "@/components/app/onboarding/phone-step";
import { ProfileStep } from "@/components/app/onboarding/profile-step";
import { RoleStep } from "@/components/app/onboarding/role-step";
import { SessionStep } from "@/components/app/onboarding/session-step";
import { BILLING_ON_WEB_META, STEP_META } from "@/components/app/onboarding/step-meta";
import {
  needsAttention,
  type TeamInviteOutcome,
} from "@/components/app/onboarding/team-invite-result";
import { TeamStep } from "@/components/app/onboarding/team-step";
import { Skeleton } from "@/components/ui/skeleton";
import { isInIosApp } from "@/lib/links/in-app";
import { productHostRedirect } from "@/lib/marketing/login-host";
import { orpc } from "@/lib/orpc";
import { endSession } from "@/lib/session/sign-out";
import { welcomeAfterLoad } from "@/lib/session/tour";

export const Route = createFileRoute("/onboarding")({
  ssr: false,
  /* Same rule as /signup: the run is the product's, on the product's host. */
  beforeLoad: ({ location }) => {
    const elsewhere = productHostRedirect(location);
    if (elsewhere) throw redirect({ href: elsewhere, replace: true });
  },
  component: OnboardingPage,
});

/** The server says which step is next (`onboarding.state`) and holds the rules; the page keeps only
 * which step is on screen, so Back keeps answers. A member joining an existing organization skips
 * the organization, team and setup session steps. */
function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useQuery(orpc.auth.me.queryOptions());
  const state = useQuery({ ...orpc.onboarding.state.queryOptions(), enabled: Boolean(me.data) });
  /* Which step is on screen once the person has moved; null means "the
     server's", so the first render lands where the run left off. */
  const [chosen, setChosen] = useState<OnboardingStep | null>(null);
  const step = chosen ?? state.data?.step ?? null;
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [teamResult, setTeamResult] = useState<TeamInviteOutcome | null>(null);

  const data = me.data;
  const elsewhere = me.isError
    ? "/login?next=%2Fonboarding"
    : !data
      ? null
      : data.user.mustResetPassword
        ? "/set-new-password"
        : (portalHomeFor(data.user.role) ??
          (data.subject.platformAdmin || !data.onboarding ? "/" : null));
  useEffect(() => {
    if (elsewhere) void navigate({ href: elsewhere, replace: true });
  }, [elsewhere, navigate]);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.onboarding.state.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.auth.me.key() }),
    ]);
  const fail = (error: Error) => {
    setProblem(error.message);
    toast.error(errorWords(error));
  };
  const advance = (next: OnboardingStep) => {
    setProblem(null);
    setChosen(next);
  };

  const setPassword = useMutation(
    orpc.onboarding.setPassword.mutationOptions({
      onSuccess: () => refresh().then(() => advance("profile")),
      onError: fail,
    }),
  );
  const saveProfile = useMutation(
    orpc.onboarding.saveProfile.mutationOptions({
      onSuccess: () => refresh().then(() => advance("intent")),
      onError: fail,
    }),
  );
  const saveIntent = useMutation(
    orpc.onboarding.saveIntent.mutationOptions({
      onSuccess: () => refresh().then(() => advance("role")),
      onError: fail,
    }),
  );
  const saveRole = useMutation(
    orpc.onboarding.saveRole.mutationOptions({
      onSuccess: () =>
        refresh().then(() => advance(state.data?.organization ? "phone" : "community")),
      onError: fail,
    }),
  );
  const createCommunity = useMutation(
    orpc.onboarding.createCommunity.mutationOptions({
      /* Next is the server's call: payment where it is owed, otherwise the team. */
      onSuccess: () => refresh().then(() => setChosen(null)),
      onError: fail,
    }),
  );
  const inviteTeam = useMutation(
    orpc.onboarding.inviteTeam.mutationOptions({
      /* Anybody left out or not emailed keeps the owner here to read who. */
      onSuccess: (result) => {
        const stay = needsAttention(result);
        if (stay) setTeamResult(result);
        else if (result.invited.length > 0) toast.success(`Invited ${result.invited.length}`);
        return refresh().then(() => advance(stay ? "team" : "phone"));
      },
      onError: fail,
    }),
  );
  const sendCode = useMutation(
    orpc.onboarding.phone.send.mutationOptions({
      onSuccess: (result) => {
        setProblem(null);
        setSentTo(result.phone);
      },
      onError: fail,
    }),
  );
  const verifyCode = useMutation(
    orpc.onboarding.phone.verify.mutationOptions({
      /* A colleague has no setup session to book: verifying finishes their run. */
      onSuccess: () =>
        state.data?.member ? complete.mutate({}) : refresh().then(() => advance("session")),
      onError: fail,
    }),
  );
  const recordSession = useMutation(
    orpc.onboarding.recordSession.mutationOptions({ onSuccess: refresh, onError: fail }),
  );
  const complete = useMutation(
    orpc.onboarding.complete.mutationOptions({
      onSuccess: () => refresh().then(() => advance("done")),
      onError: fail,
    }),
  );

  const current = state.data;
  if (!data || !current || !step || elsewhere) {
    return (
      <div className="bg-background min-h-dvh px-4 py-16">
        <div className="mx-auto w-full max-w-2xl">
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  const visible: OnboardingStep[] = current.member
    ? ["password", "profile", "intent", "role", "phone", "done"]
    : [
        "password",
        "profile",
        "intent",
        "role",
        "community",
        ...(current.billing?.required || current.billing?.paid ? (["payment"] as const) : []),
        "team",
        "phone",
        "session",
        "done",
      ];
  const index = Math.max(0, visible.indexOf(step));
  /* Back revisits answered steps only: a set password and a made
     organization are not unmade. */
  const previous = index > 0 ? visible[index - 1] : null;
  const canGoBack =
    previous !== null &&
    previous !== "password" &&
    previous !== "payment" &&
    !(previous === "community" && current.organization) &&
    step !== "done";
  /* No payment is taken inside the iPhone app (App Store 3.1.1): billing is finished on the web. */
  const billingOnWeb = step === "payment" && isInIosApp();
  const meta = billingOnWeb ? BILLING_ON_WEB_META : STEP_META[step];
  const pending =
    setPassword.isPending ||
    saveProfile.isPending ||
    saveIntent.isPending ||
    saveRole.isPending ||
    createCommunity.isPending ||
    inviteTeam.isPending ||
    sendCode.isPending ||
    verifyCode.isPending ||
    complete.isPending;

  const openWorkspace = () => {
    queryClient.clear();
    // A full load, so every provider reads the new organization from the start.
    welcomeAfterLoad();
    window.location.assign("/");
  };

  return (
    <OnboardingFrame
      icon={meta.icon}
      stepIndex={index}
      stepCount={visible.length}
      title={
        step === "password"
          ? `Welcome. ${meta.title}`
          : step === "done"
            ? `${meta.title}, ${current.user.name.split(" ")[0] ?? ""}`
            : meta.title
      }
      subtitle={meta.subtitle}
      onBack={canGoBack && previous ? () => advance(previous) : undefined}
      onSignOut={() => void endSession(queryClient)}
    >
      {step === "password" ? (
        <PasswordStep
          email={current.user.email}
          pending={pending}
          onSubmit={(password) => setPassword.mutate({ password })}
        />
      ) : null}
      {step === "profile" ? (
        <ProfileStep
          initialName={current.user.name}
          pending={pending}
          onSubmit={(name) => saveProfile.mutate({ name })}
        />
      ) : null}
      {step === "intent" ? (
        <IntentStep
          initial={current.intent as Parameters<typeof IntentStep>[0]["initial"]}
          pending={pending}
          onSubmit={(intent) => saveIntent.mutate({ intent })}
        />
      ) : null}
      {step === "role" ? (
        <RoleStep
          initial={current.workRole as Parameters<typeof RoleStep>[0]["initial"]}
          pending={pending}
          onSubmit={(workRole) => saveRole.mutate({ workRole })}
        />
      ) : null}
      {step === "community" ? (
        <CommunityStep
          suggestedOrganizationName={current.suggestedOrganizationName}
          pending={pending}
          onSubmit={(values) => createCommunity.mutate(values)}
        />
      ) : null}
      {billingOnWeb ? <BillingOnWeb onCheck={refresh} /> : null}
      {step === "payment" && !billingOnWeb ? (
        <PaymentStep onPaid={() => refresh().then(() => advance("team"))} />
      ) : null}
      {step === "team" ? (
        <TeamStep
          result={teamResult}
          onResultSeen={() => {
            setTeamResult(null);
            advance("phone");
          }}
          claimableDomain={current.claimableDomain}
          initialDomainJoin={Boolean(current.organization?.autoJoinDomain)}
          roleTemplates={current.roleTemplates}
          clinicalRoles={current.clinicalRoles}
          pending={pending}
          onSubmit={(values) => inviteTeam.mutate(values)}
          onSkip={() => inviteTeam.mutate({ people: [], allowDomainJoin: false })}
        />
      ) : null}
      {step === "phone" ? (
        <PhoneStep
          initialPhone={current.user.phone}
          smsAvailable={current.smsAvailable}
          sentTo={sentTo}
          pending={pending}
          problem={problem}
          onSend={(phone) => (phone ? sendCode.mutate({ phone }) : setSentTo(null))}
          onVerify={(phone, code) => verifyCode.mutate({ phone, code })}
        />
      ) : null}
      {step === "session" ? (
        <SessionStep
          schedulerUrl={current.schedulerUrl}
          name={current.user.name}
          email={current.user.email}
          phone={current.user.phone}
          booked={Boolean(current.session)}
          pending={pending}
          onBooked={(booking) => recordSession.mutate(booking)}
          onContinue={() => complete.mutate({})}
        />
      ) : null}
      {step === "done" ? (
        <DoneStep
          organizationName={current.organization?.name ?? null}
          invitedCount={current.invitations.length}
          sessionBooked={Boolean(current.session)}
          onOpen={() =>
            current.completedAt
              ? openWorkspace()
              : complete.mutate({}, { onSuccess: openWorkspace })
          }
        />
      ) : null}
    </OnboardingFrame>
  );
}
